/* eslint-disable @typescript-eslint/no-loop-func */
import { Flex, Text, Tooltip } from '@mantine/core';
import {
  CellClickedEvent,
  ColDef,
  ColumnEvent,
  SortChangedEvent,
  ColumnState,
  DetailGridInfo,
  GetDetailRowDataParams,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  IDetailCellRendererParams,
  InitialGroupOrderComparatorParams,
  IRowNode,
  ModuleRegistry,
  RowDataUpdatedEvent,
  RowGroupOpenedEvent,
  RowNode,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useAtom, useAtomValue, useSetAtom, useStore } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AllCommunityModule, ClientSideRowModelModule } from 'ag-grid-community';
import { MasterDetailModule } from 'ag-grid-enterprise';
import { ColumnMenuModule, ContextMenuModule } from 'ag-grid-enterprise';
import { ColumnsToolPanelModule, GroupFilterModule, PivotModule, RowGroupingModule, RowGroupingPanelModule, TreeDataModule } from 'ag-grid-enterprise';
import { CellSelectionModule } from 'ag-grid-enterprise';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useDidUpdate, useDisclosure, useLocalStorage, useThrottledCallback, useToggle } from '@mantine/hooks';
import {
  BrokerNerdsBarkerCoreEnumsPlanType,
  BrokerNerdsBarkerCoreEnumsPointOfSale,
  BrokerNerdsBarkerCoreEnumsPricerStatus,
  BrokerNerdsBarkerCoreModelsInventoryEvent,
  BrokerNerdsBarkerCoreModelsInventoryEventCommentReceipt,
  BrokerNerdsBarkerCoreModelsInventoryListingVendorPropertiesDtiPortal,
  BrokerNerdsBarkerCoreModelsSalesListingSoldVendorPropertiesTradeDeskPos,
  getGetApiInventoryEventsCommentsReceiptsQueryKey,
  getGetApiSalesQueryKey,
  SystemTextJsonPatchOperationsOperation,
  useGetApiAccountingPlansCurrent,
  useGetApiInventoryEventsCommentsReceipts,
  useGetApiInventoryVendors,
  useGetApiSales,
  usePutApiInventoryEventsEventIdViewed,
  usePutApiInventoryListingsListingIdViewed,
} from '../../api';
import { BarkerEventListing, BarkerEventListingWithPending, FilterMode } from '../../types';
import {
  filterModeAtom,
  mergedEventListingsAtom,
  mergedEventListingsUnfilteredRulesAtom,
  pendingListingUpdatesAtom,
  previousSelectedEventAtom,
  previousSelectedTenantListingIdAtom,
  rowDraggingAtom,
  selectedEventAtom,
  selectedMergedListingAtom,
  selectedTenantListingIdAtom,
  showEventPerformanceAtom,
  transientGlobalStateAtom,
  updateListingsAtom,
} from '../../data/atoms';
import { useInventoryDragEvents } from './Inventory.dragdrop';
import { RePrice } from './Inventory.RePrice';
import { useRuleState } from '../../data/Rule.state';
import { formatCurrency } from '../../utils/formatters';
import { Properties } from './Inventory.Properties';
import yasml from '@thirtytech/yasml';
import { InventoryAction } from './Inventory.Action';
import { ListingIdDisplay } from './Inventory.ListingIdDisplay';
import { useGlobalState } from '../../data/Global.state';
import { SortItem } from './Inventory.SortItem';
import { useSearchParams } from 'react-router-dom';
import { BNTags } from '../../components/Tags/Tags';
import { matchesRowFilter } from '../MarketListings/marketListingsFilter';
import { DragCell } from './Inventory.DragCell';
import { useBulkState } from '../../data/Bulk.state';
import { InlineSplitsToggle } from './Inventory.InlineSplits';
import { getPointOfSaleSplitType, setSplitsModelForUpdate } from '../../components/Splits/Splits.utils';
import { InventoryOnHold } from './Inventory.OnHold';
import { Broadcasting } from './Inventory.Broadcasting';
import { SaleDetails } from './Inventory.SaleDetails';
import { BNEmptyState } from '../../components/EmptyState/EmptyState';
import { useListingState } from '../../data/Listing.state';
import { TenantIdListingId } from '../../models/tenantIdListingId';
import { useAppearanceSettings } from './Inventory.AppearanceSettings.hook';
import { useFlag } from '@unleash/proxy-client-react';
import { PriceDisplay } from './Inventory.PriceDisplay';
import { openConfirmModal } from '@mantine/modals';
import BarcodeIcon from '../../components/icons/Barcode';
import { onRefetchInventory } from '../EventSearch/SearchResults.hooks';
import { Attachments } from '../DTI/Attachments/Attachments';
import { isDtiHosted } from '../../utils/whitelabel-consts';
import { range } from '../../utils/price-utils';
import { DTIOnHoldProperty } from '../DTI/OnHold/DTIOnHoldProperty';
import { AxiosResponse } from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_INVENTORY_EVENT_SETTINGS,
  INVENTORY_EVENT_SETTINGS_ITEMS,
  INVENTORY_EVENT_SETTINGS_STORAGE_KEY,
  InventoryEventSettings,
  normalizeInventoryEventSettings,
} from './Inventory.EventSettings';
import { compareInventoryEventGroups } from './Inventory.EventSort';

dayjs.extend(relativeTime);
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  AllCommunityModule,
  MasterDetailModule,
  ColumnMenuModule,
  ContextMenuModule,
  ColumnsToolPanelModule,
  RowGroupingPanelModule,
  RowGroupingModule,
  GroupFilterModule,
  TreeDataModule,
  PivotModule,
  CellSelectionModule,
]);

export const onNextInventoryItem = new Event('onNextInventoryItem');
export const onPrevInventoryItem = new Event('onPrevInventoryItem');
export const onColumnMoved = new Event('onColumnMoved');
export const onColumnSorted = new Event('onColumnSorted');

const InventoryState = () => {
  const gridRef = useRef<AgGridReact<BarkerEventListing>>(null);
  const detailGridRef = useRef<Partial<AgGridReact<BarkerEventListing>> | undefined>(undefined);
  const [gridReady, setGridReady] = useState(false);
  const { currentUser, tenants, selectedTenantIds } = useGlobalState('currentUser', 'tenants', 'selectedTenantIds');
  const { patchListing } = useListingState('patchListing');
  const [showSort, toggleShowSort] = useToggle();
  const [selectedListingId, setSelectedListingId] = useAtom(selectedTenantListingIdAtom);
  const selectedListing = useAtomValue(selectedMergedListingAtom);
  const [inventoryQuickFilter, setInventoryQuickFilter] = useState<string>();
  const [filter, setFilter] = useState(''); // Used for input state of semi controlled input
  const [transientGlobalState, setTransientGlobalState] = useAtom(transientGlobalStateAtom);
  const pendingUpdates = useAtomValue(pendingListingUpdatesAtom);
  const [searchParams] = useSearchParams();
  const selectedEvent = useAtomValue(selectedEventAtom);
  const { setShowBulkOptions, showBulkOptions } = useBulkState('showBulkOptions', 'setShowBulkOptions');
  const isShowingEventPerformance = useAtomValue(showEventPerformanceAtom);
  const previousSelectedListingId = useAtomValue(previousSelectedTenantListingIdAtom);
  const previousSelectedEvent = useAtomValue(previousSelectedEventAtom);
  const appearanceSettings = useAppearanceSettings();
  const [showPurchaseModal, { open: openPurchaseModal, close: _closePurchaseModal }] = useDisclosure();
  const [initialPurchaseEvent, setInitialPurchaseEvent] = useState<BrokerNerdsBarkerCoreModelsInventoryEvent>();

  const selectedVL1Tenants = tenants?.filter((t) => t.pointOfSaleId === 'VictoryLiveOne' && selectedTenantIds?.includes(t.tenantId));

  const singleVL1Tenant = selectedVL1Tenants?.length === 1 ? selectedVL1Tenants[0] : undefined;
  const { data: currentPlans } = useGetApiAccountingPlansCurrent({
    query: {
      enabled: selectedTenantIds?.length > 0,
      select(data) {
        return data.data;
      },
    },
  });
  const manualPricingOnlyTenantIds = useMemo(
    () =>
      new Set(
        currentPlans?.filter((tenantPlan) => tenantPlan.plan.planTypeId === BrokerNerdsBarkerCoreEnumsPlanType.ManualPricingOnly).map((tenantPlan) => tenantPlan.tenantId) ?? [],
      ),
    [currentPlans],
  );
  const manualPricingOnlyTenantIdsRef = useRef(manualPricingOnlyTenantIds);
  const isManualPricingOnlyTenant = useCallback((tenantId?: string) => !!tenantId && manualPricingOnlyTenantIds.has(tenantId), [manualPricingOnlyTenantIds]);

  const hasTestingOverride = selectedTenantIds.every((t) => t === 'b9ed916e-f31f-4a68-b8bb-b09edd9ea3e6');

  const hasCreatePoFlag = useFlag('vl1-create-po');

  const hasTicketStockPropertyFlag = useFlag('ticket-stock-property');

  const { data: vendors, isLoading: isVendorsLoading } = useGetApiInventoryVendors(
    {},
    {
      query: {
        enabled: selectedTenantIds?.length > 0 && ((!!singleVL1Tenant && hasCreatePoFlag) || hasTestingOverride),
        select(data) {
          return data.data;
        },
      },
    },
  );

  const closePurchaseModal = useCallback(
    (bypass: boolean = false) => {
      if (bypass) {
        _closePurchaseModal();
        setInitialPurchaseEvent(undefined);
        document.dispatchEvent(onRefetchInventory);
        return;
      }
      openConfirmModal({
        title: 'Are you sure you want to quit?',
        children: (
          <Text size="sm" pb="xs">
            You will lose any information entered for new inventory.
          </Text>
        ),
        labels: { confirm: 'Quit & Cancel Order', cancel: 'Keep Editing' },
        confirmProps: { className: 'confirmButton', variant: 'filled', color: 'red', size: 'sm' },
        cancelProps: { className: 'cancelButton', variant: 'default', color: 'gray', size: 'sm' },
        closeButtonProps: { size: 'md' },
        onConfirm() {
          _closePurchaseModal();
          setInitialPurchaseEvent(undefined);
        },
      });
    },
    [_closePurchaseModal],
  );

  const hideCostFlag = useFlag('hide-cost');

  const toggleBulkOptions = useCallback(() => {
    setShowBulkOptions((p) => !p);
  }, [setShowBulkOptions]);
  const defaultColDef = useMemo<ColDef>(
    () => ({
      width: 170,
      flex: 1,
      resizable: showSort,
      suppressHeaderMenuButton: true,
      suppressMovable: !showSort,
      sortable: showSort,
      suppressHeaderFilterButton: true,
      filter: true,
    }),
    [showSort],
  );
  const [inventoryGridState, setInventoryGridState, clearInventoryGridState] = useLocalStorage<ColumnState[]>({
    key: 'inventory-grid-state',
    getInitialValueInEffect: false,
  });

  const [eventGridState, setEventGridState, clearEventGridState] = useLocalStorage<SortItem[]>({
    key: 'inventory-grid-event-sort',
    getInitialValueInEffect: false,
  });
  const [eventHeaderSettingsRaw, setEventHeaderSettingsRaw] = useLocalStorage<InventoryEventSettings>({
    key: INVENTORY_EVENT_SETTINGS_STORAGE_KEY,
    defaultValue: DEFAULT_INVENTORY_EVENT_SETTINGS,
    getInitialValueInEffect: false,
  });
  const eventHeaderSettings = useMemo(() => normalizeInventoryEventSettings(eventHeaderSettingsRaw), [eventHeaderSettingsRaw]);

  const hasAllEventHeaderSettings = useMemo(() => {
    if (!eventHeaderSettingsRaw) {
      return false;
    }

    return INVENTORY_EVENT_SETTINGS_ITEMS.every(({ key }) => typeof eventHeaderSettingsRaw[key] === 'boolean');
  }, [eventHeaderSettingsRaw]);

  const setEventHeaderSettings = useCallback(
    (value: InventoryEventSettings | ((previous: InventoryEventSettings) => InventoryEventSettings)) => {
      setEventHeaderSettingsRaw((previous) => {
        const previousValue = normalizeInventoryEventSettings(previous);
        const nextValue = typeof value === 'function' ? value(previousValue) : value;
        return normalizeInventoryEventSettings(nextValue);
      });
    },
    [setEventHeaderSettingsRaw],
  );

  const resetEventHeaderSettings = useCallback(() => {
    setEventHeaderSettings(DEFAULT_INVENTORY_EVENT_SETTINGS);
  }, [setEventHeaderSettings]);

  useEffect(() => {
    const hasStorageValue = typeof window !== 'undefined' && window.localStorage.getItem(INVENTORY_EVENT_SETTINGS_STORAGE_KEY) !== null;
    if (!hasStorageValue || !hasAllEventHeaderSettings) {
      setEventHeaderSettingsRaw(eventHeaderSettings);
    }
  }, [eventHeaderSettings, hasAllEventHeaderSettings, setEventHeaderSettingsRaw]);

  useEffect(() => {
    if (searchParams.has('resetGrid')) {
      clearInventoryGridState();
      clearEventGridState();
      resetEventHeaderSettings();
    }
  }, [clearEventGridState, clearInventoryGridState, resetEventHeaderSettings, searchParams]);

  const { validateDirtyRuleState, triggerSaveRule } = useRuleState('validateDirtyRuleState', 'triggerSaveRule');
  const { onDetailMasterRowDragEnd, onMasterRowDragEnd, onMasterRowDragMove, onDetailRowDragEnd } = useInventoryDragEvents({
    gridRef,
    detailGridRef,
    isManualPricingOnlyTenant,
  });

  const updateInventoryListing = useSetAtom(updateListingsAtom);

  const mergedEventListings = useAtomValue(mergedEventListingsAtom);
  const [filteredListings, setFilteredListings] = useState<BarkerEventListing[]>(mergedEventListings);

  useDidUpdate(() => {
    if (inventoryQuickFilter) {
      let matchingListings = [...mergedEventListings];

      const andParts = inventoryQuickFilter.split(' ');

      andParts.forEach((andPart) => {
        const andListings: BarkerEventListing[] = [];
        // Dedup within an AND group: a listing matching multiple OR parts (or matched on both
        // section and row) should appear once. This also replaces `push(...bigArray)`, which both
        // produced duplicate rows downstream (fed to AG-Grid's getRowId) and risked a call-stack
        // overflow when spreading a large filter result into push.
        const seenListingIds = new Set<string>();
        const addMatches = (matched: BarkerEventListing[]) => {
          for (const listing of matched) {
            if (!seenListingIds.has(listing.listingId)) {
              seenListingIds.add(listing.listingId);
              andListings.push(listing);
            }
          }
        };

        const orParts = andPart.split(',');

        orParts.forEach((orPart) => {
          const rangeParts = orPart.split('-');
          // Hoist invariants out of the per-listing filter callbacks below so they aren't
          // recomputed for every listing (orPart.toLowerCase() was being called up to 5x per row).
          const orPartArr = [orPart];
          const needle = orPart.toLowerCase();

          if (
            rangeParts.length === 2 &&
            ((!isNaN(Number(rangeParts[0])) && !isNaN(Number(rangeParts[1]))) ||
              ([...rangeParts[0]].every((char) => char === rangeParts[0][0]) && [...rangeParts[1]].every((char) => char === rangeParts[1][0])))
          ) {
            // Either the part is a number of it is a string of the same character, so check sections or rows
            addMatches(matchingListings.filter((listing) => matchesRowFilter(listing.section, orPartArr) || matchesRowFilter(listing.row, orPartArr)));
          } else if (orPart.length <= 3 && (!isNaN(Number(orPart)) || [...orPart].every((char) => char === orPart[0]))) {
            // Either the part is a number or it is a string of the same character, so check sections or rows
            addMatches(matchingListings.filter((listing) => matchesRowFilter(listing.section, orPartArr) || matchesRowFilter(listing.row, orPartArr)));
          } else {
            // Otherwise, check event name, venue name, city, state, or section
            addMatches(
              matchingListings.filter(
                (listing) =>
                  listing.event.name.toLowerCase().includes(needle) ||
                  listing.event.venue.name.toLowerCase().includes(needle) ||
                  listing.event.venue.city?.toLowerCase().includes(needle) ||
                  listing.event.venue.state?.toLowerCase().includes(needle) ||
                  listing.section.toLowerCase().includes(needle),
              ),
            );
          }
        });

        matchingListings = andListings;
      });

      // Pull in rule siblings of any matched listing: a listing sharing a ruleId with a match but
      // not already matched should be included. The previous version scanned every match for each of
      // the n listings (O(n·m)) and re-scanned on each addition; precomputed sets make it O(n+m).
      const matchedListingIds = new Set(matchingListings.map((match) => match.listingId));
      const matchedRuleIds = new Set<string | undefined>();
      for (const match of matchingListings) {
        if (match.ruleId !== null) {
          matchedRuleIds.add(match.ruleId);
        }
      }

      for (const listing of mergedEventListings) {
        if (listing.ruleId !== null && matchedRuleIds.has(listing.ruleId) && !matchedListingIds.has(listing.listingId)) {
          matchingListings.push(listing);
          matchedListingIds.add(listing.listingId);
        }
      }

      setFilteredListings([...matchingListings]);
    } else {
      setFilteredListings(mergedEventListings);
    }
    // selectedListingId intentionally omitted: the quick-filter result depends only on the filter
    // text and the listings. Including it forced a full re-filter of every listing on each row
    // selection (selecting a row that mutates the listings is already covered by mergedEventListings).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryQuickFilter, mergedEventListings]);

  const { mutate: updateLastViewedEventId } = usePutApiInventoryEventsEventIdViewed({
    axios: {
      headers: {
        'x-tenant-id': selectedListing?.tenantId,
      },
    },
  });
  const { mutate: updateLastViewedListingId } = usePutApiInventoryListingsListingIdViewed({
    axios: {
      headers: {
        'x-tenant-id': selectedListing?.tenantId,
      },
    },
  });

  const closeExpandedNode = useCallback((tenantIdListingId: TenantIdListingId) => {
    gridRef.current?.api.getRowNode(tenantIdListingId.toString())?.setExpanded(false);
  }, []);

  const mergedEventListingsRef = useRef(mergedEventListings);
  const tenantsRef = useRef(tenants);

  useEffect(() => {
    mergedEventListingsRef.current = mergedEventListings;
  }, [mergedEventListings]);

  useEffect(() => {
    tenantsRef.current = tenants;
  }, [tenants]);

  useEffect(() => {
    manualPricingOnlyTenantIdsRef.current = manualPricingOnlyTenantIds;
    const gridApi = gridRef.current?.api;
    if (!gridApi) {
      return;
    }
    gridApi.refreshCells({
      columns: ['dragCell'],
      force: true,
    });
  }, [manualPricingOnlyTenantIds]);

  // Note: Function trapped in grid. Can't useCallback or mergedEventListings will always be empty.
  const updateBroadcasting = async (tenantId: string, listingId: string, isBroadcasting: boolean) => {
    const listing = mergedEventListingsRef.current?.find((l) => l.tenantId === tenantId && l.listingId === listingId);
    const updates: SystemTextJsonPatchOperationsOperation[] = [
      {
        op: 'Replace',
        path: '/isBroadcasting',
        value: isBroadcasting,
      },
    ];
    if (isBroadcasting && !listing?.splits) {
      const tenantPointOfSale = tenantsRef.current?.find((t) => t.tenantId === listing?.tenantId)?.pointOfSaleId;
      if (tenantPointOfSale) {
        const splitUpdate = setSplitsModelForUpdate(tenantPointOfSale, getPointOfSaleSplitType(tenantPointOfSale, listing?.splits));
        if (splitUpdate) {
          updates.push(splitUpdate);
        }
      }
    }
    const response = await patchListing({
      tenantId,
      listingId,
      data: updates,
    });
    updateInventoryListing(response.data);
  };

  interface DragCellRendererParams extends ICellRendererParams<BarkerEventListing> {
    showBulkOptions: boolean;
  }

  type BNInventoryColumn = ColDef<BarkerEventListing> & {
    exclude?: boolean;
    allowSorting?: boolean;
    sort?: 'asc' | 'desc' | null;
    sortIndex?: number | null;
  };
  const [selectedHistoryListing, setSelectedHistoryListing] = useState<{
    listing: BarkerEventListing | null;
    isOpen: boolean;
  }>({ listing: null, isOpen: false });

  const isTradeDeskUser = tenants?.some((t) => t.pointOfSaleId === BrokerNerdsBarkerCoreEnumsPointOfSale.TradeDeskPos) ?? false;

  // @ts-ignore
  const dtiDefaultColumnDefs: BNInventoryColumn[] = useMemo(() => {
    if (!isDtiHosted) {
      return [];
    }

    if (tenants === undefined || !tenants.some((t) => t.pointOfSaleId === 'DtiPortal')) {
      return [];
    }

    return [
      {
        colId: 'attachments',
        allowSorting: false,
        headerName: 'Assets',
        enableCellChangeFlash: false,
        valueGetter: (params) => {
          const vendorProperties = params.data?.vendorProperties;
          return `${params.data?.quantityRemaining}|${vendorProperties ? JSON.stringify(vendorProperties) : ''}`;
        },
        cellRenderer: (params: ICellRendererParams<BarkerEventListing>) => {
          if (params.data && !params.data?.listingId.endsWith('sales') && !params.data?.sale) {
            const { vendorProperties } = params.data;

            const dtiProperties =
              vendorProperties?.pointOfSaleId === BrokerNerdsBarkerCoreEnumsPointOfSale.DtiPortal
                ? (vendorProperties as BrokerNerdsBarkerCoreModelsInventoryListingVendorPropertiesDtiPortal)
                : undefined;

            if (!dtiProperties) {
              return null;
            }

            return (
              <Attachments
                numBarcodes={dtiProperties.numBarcodes}
                numPdfs={dtiProperties.numPdfs}
                num1Tmds={dtiProperties.num1TMDs}
                numTransferUrls={dtiProperties.numTransferUrls}
              />
            );
          }
          return null;
        },
        width: 120,
        minWidth: 120,
      },
      {
        // @ts-ignore
        field: 'vendorProperties.location',
        headerName: 'Location',
        width: 150,
        hide: true,
      },
      {
        // @ts-ignore
        field: 'vendorProperties.purchaserEmail',
        headerName: 'Purchaser',
        width: 200,
        hide: true,
        // @ts-ignore
        tooltipField: 'vendorProperties.purchaserEmail',
      },
      {
        // @ts-ignore
        field: 'vendorProperties.accountInfo',
        headerName: 'Account Info',
        width: 200,
        hide: true,
        // @ts-ignore
        tooltipField: 'vendorProperties.accountInfo',
      },
      {
        // @ts-ignore
        field: 'vendorProperties.orderNumber',
        headerName: 'Order #',
        width: 200,
        hide: true,
        // @ts-ignore
        tooltipField: 'vendorProperties.orderNumber',
      },
      {
        // @ts-ignore
        colId: 'cardInfo',
        headerName: 'CC Info',
        valueGetter: (params) => {
          if (params.data && params.data.vendorProperties?.pointOfSaleId === BrokerNerdsBarkerCoreEnumsPointOfSale.DtiPortal) {
            const { ccCardInfo, ccPurchaserInfo } = params.data.vendorProperties as BrokerNerdsBarkerCoreModelsInventoryListingVendorPropertiesDtiPortal;
            return `${ccCardInfo === '0' ? '' : (ccCardInfo ?? '')} ${ccPurchaserInfo ?? ''}`.trim();
          }
          return '';
        },
        width: 150,
        hide: true,
        // @ts-ignore
        tooltipValueGetter: (params) => {
          if (params.data && params.data.vendorProperties?.pointOfSaleId === BrokerNerdsBarkerCoreEnumsPointOfSale.DtiPortal) {
            const { ccCardInfo, ccPurchaserInfo } = params.data.vendorProperties as BrokerNerdsBarkerCoreModelsInventoryListingVendorPropertiesDtiPortal;
            return `${ccCardInfo === '0' ? '' : (ccCardInfo ?? '')} ${ccPurchaserInfo ?? ''}`.trim();
          }
          return '';
        },
      },
    ] satisfies BNInventoryColumn[];
  }, [tenants]);

  const defaultColumnDefs: BNInventoryColumn[] = useMemo(
    () =>
      [
        { field: 'tenantIdEventId', allowSorting: false, rowGroup: true, hide: true, exclude: true, sortable: false },
        {
          colId: 'dragCell',
          minWidth: 38,
          sortable: false,
          allowSorting: false,
          width: 38,
          maxWidth: 38,
          rowDrag: false,
          enableCellChangeFlash: false,
          exclude: true,
          cellClass: (params) => (manualPricingOnlyTenantIdsRef.current.has(params.data?.tenantId ?? '') ? 'manual-pricing-no-drag-cell' : undefined),
          cellRendererParams: {
            showBulkOptions,
          },
          valueGetter: (params) => `${params.data?.ruleCount}|${showBulkOptions}|${manualPricingOnlyTenantIdsRef.current.has(params.data?.tenantId ?? '')}`,
          cellRenderer: (params: DragCellRendererParams) => {
            if (!params.data?.sale && params.data) {
              return (
                <DragCell
                  listingId={params.data.listingId}
                  tenantId={params.data?.tenantId}
                  isManualPricingOnlyPlan={manualPricingOnlyTenantIdsRef.current.has(params.data?.tenantId ?? '')}
                  bulkMode={params.showBulkOptions}
                  node={params.node}
                  api={params.api}
                  level={params.node.level}
                  ruleCount={params.data.ruleCount || 0}
                  isListingOnHold={params.data.quantityReserved > 0}
                  registerRowDragger={params.registerRowDragger}
                />
              );
            }
            return null;
          },
          cellStyle: { textAlign: 'left' },
        },
        {
          field: 'section',
          headerName: 'Section',
          rowDrag: false,
          minWidth: 80,
          width: 160,
          sort: 'asc',
          sortIndex: 0,
          cellStyle: { textAlign: 'left' },
          comparator: (a, b) => a?.localeCompare(b, undefined, { numeric: true }),
          tooltipField: 'section',
        },
        {
          field: 'listingId',
          headerName: 'Listing ID',
          minWidth: 90,
          width: 90,
          enableCellChangeFlash: false,
          comparator: (_valueA, _valueB, nodeA, nodeB) => (nodeA.data?.listingId || '').localeCompare(nodeB.data?.listingId || ''),
          valueGetter: (params) => `${params.data?.listingId}|${params.data?.ruleId}`,
          cellStyle: (params) => ({
            textAlign: 'center',
            color: import.meta.env.DEV && params.data?.ruleId ? 'var(--colors-brandcolor-5)' : '',
          }),
          cellRenderer: (params: ICellRendererParams<BarkerEventListing>) => <ListingIdDisplay listing={params.data!} />,
          hide: true,
        },
        {
          field: 'row',
          headerName: 'Row',
          minWidth: 70,
          width: 70,
          sort: 'asc',
          sortIndex: 1,
          cellStyle: { textAlign: 'center' },
          comparator: (a, b) => a?.localeCompare(b, undefined, { numeric: true }),
        },
        {
          colId: 'seats',
          minWidth: 90,
          width: 90,
          headerName: 'Seats',
          cellStyle: { textAlign: 'center' },
          valueGetter: (params) => {
            if (params.data) {
              const { seatFrom, seatThru, quantityRemaining } = params.data;
              return quantityRemaining > 1 ? `${seatFrom}-${seatThru}` : seatFrom;
            }

            return '';
          },
        },
        {
          field: 'quantityRemaining',
          minWidth: 70,
          width: 70,
          headerName: 'Qty',
          sort: 'desc',
          sortIndex: 2,
          cellStyle: { textAlign: 'center' },
        },
        {
          minWidth: 100,
          width: 100,
          colId: 'splits',
          headerName: 'Splits',
          cellStyle: { textAlign: 'center', textTransform: 'capitalize' },
          hide: true,
          valueGetter: (params) => {
            if (params.data && !params.data?.listingId.endsWith('sales')) {
              return JSON.stringify({ splits: params.data.splits });
            }
            return '';
          },
          cellRenderer: (params: ICellRendererParams<BarkerEventListing>) => {
            if (params.data?.listingId.endsWith('sales') || params.data?.sale) {
              return null;
            }
            return params.data ? <InlineSplitsToggle listing={params.data} /> : params.value;
          },
        },
        {
          field: 'isBroadcasting',
          headerName: 'Broadcasting',
          hide: true,
          minWidth: 50,
          width: 50,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center', paddingLeft: 0, paddingRight: 0 },
          cellRenderer: (params: ICellRendererParams<BarkerEventListing>) => {
            if (params.data) {
              const { isBroadcasting, listingId, tenantId, ruleCount } = params.data;
              return <Broadcasting isBroadcasting={isBroadcasting} listingId={listingId} tenantId={tenantId} updateBroadcasting={updateBroadcasting} ruleCount={ruleCount} />;
            }
            return null;
          },
        },
        {
          field: 'quantityReserved',
          headerName: 'On Hold',
          hide: true,
          minWidth: 50,
          width: 50,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center', paddingLeft: 0, paddingRight: 0 },
          cellRenderer: (params: ICellRendererParams<BarkerEventListing>) => {
            if (params.data) {
              const { quantityRemaining, quantityReserved } = params.data;

              if (isDtiHosted && params.data.vendorProperties?.pointOfSaleId === 'DtiPortal') {
                const { ownerId } = params.data.vendorProperties as BrokerNerdsBarkerCoreModelsInventoryListingVendorPropertiesDtiPortal;
                const { listingId } = params.data;
                return <DTIOnHoldProperty quantityReserved={quantityReserved} quantityRemaining={quantityRemaining} accountId={ownerId} listingId={listingId} />;
              }

              return <InventoryOnHold quantityRemaining={quantityRemaining} quantityReserved={quantityReserved} />;
            }

            return null;
          },
        },
        {
          field: 'unitCost',
          minWidth: 110,
          width: 110,
          headerName: 'Cost',
          valueFormatter: (params) => formatCurrency(params.value),
          cellStyle: { textAlign: 'right', fontVariantNumeric: 'tabular-nums', letterSpacing: -0.65 },
          exclude: hideCostFlag,
          hide: hideCostFlag ? true : undefined,
        },
        {
          field: 'unitFace',
          minWidth: 110,
          width: 110,
          headerName: 'Face',
          valueFormatter: (params) => formatCurrency(params.value),
          cellStyle: { textAlign: 'right', fontVariantNumeric: 'tabular-nums', letterSpacing: -0.65 },
          hide: true,
        },
        {
          field: 'unitPrice',
          minWidth: 110,
          width: 110,
          headerName: 'Price',
          suppressKeyboardEvent: () => true,
          comparator: (_valueA, _valueB, nodeA, nodeB) => (nodeA.data?.unitPrice || 0) - (nodeB.data?.unitPrice || 0),
          valueGetter: (params) => `${params.data?.unitPrice}|${params.data?.pricerStatusId}`,
          cellStyle: { textAlign: 'right', fontVariantNumeric: 'tabular-nums', letterSpacing: -0.65 },
          cellRenderer: (params: ICellRendererParams<BarkerEventListing>) => {
            if (params.data && !params.data?.listingId.endsWith('sales')) {
              return (
                <PriceDisplay
                  gridRef={gridRef}
                  isDetailRow={params.node.level === 0}
                  listingId={params.data.listingId}
                  tenantId={params.data.tenantId}
                  pricerStatusId={params.data.pricerStatusId}
                  value={params.data.unitPrice}
                  cost={params.data.unitCost}
                  isListingOnHold={params.data.quantityReserved > 0}
                />
              );
            }
            return null;
          },
        },
        {
          minWidth: 100,
          width: 100,
          colId: 'repriced',
          headerName: 'Repriced',
          comparator: (_valueA, _valueB, nodeA, nodeB) => {
            const { pricedAt: ApricedAt, posPricedAt: AposPricedAt } = nodeA?.data || ({} as BarkerEventListing);
            const { pricedAt: BpricedAt, posPricedAt: BposPricedAt } = nodeB?.data || ({} as BarkerEventListing);
            const AposNewerThanPricedAt = !ApricedAt || dayjs(AposPricedAt).isAfter(ApricedAt);
            const BposNewerThanPricedAt = !BpricedAt || dayjs(BposPricedAt).isAfter(BpricedAt);
            const ADate = AposNewerThanPricedAt ? AposPricedAt : ApricedAt;
            const BDate = BposNewerThanPricedAt ? BposPricedAt : BpricedAt;
            return (
              // @ts-ignore
              // @ts-ignore
              dayjs(ADate || new Date(0))
                .toDate()
                .getTime() -
              dayjs(BDate || new Date(0))
                .toDate()
                .getTime()
            );
          },
          valueGetter: (params) => {
            if (params.data) {
              const { pricedAt, posPricedAt, previousPrice, unitPrice, latestPrice } = params.data;
              const posNewerThanPricedAt = !pricedAt || dayjs(posPricedAt).isAfter(pricedAt);
              return `${previousPrice}|${unitPrice}|${posNewerThanPricedAt ? posPricedAt : pricedAt}|${latestPrice}`;
            }
            return '0|0||0';
          },
          cellRenderer: (params: ICellRendererParams) => (
            <RePrice
              posPricedAt={params.data.posPricedAt}
              pricedAt={params.data.pricedAt}
              unitPrice={params.data.unitPrice}
              previousPrice={params.data.previousPrice}
              pricedBy={params.data.pricedBy}
              latestPrice={params.data.latestPrice}
            />
          ),
          cellStyle: { textAlign: 'center' },
        },
        {
          colId: 'repricedDetails',
          headerName: 'Repriced Text',
          hide: true,
          minWidth: 120,
          allowSorting: false,
          valueGetter: (params) => {
            if (params.data) {
              const { pricedAt, posPricedAt, previousPrice, unitPrice, latestPrice } = params.data;
              const posNewerThanPricedAt = !pricedAt || dayjs(posPricedAt).isAfter(pricedAt);
              return `${previousPrice}|${unitPrice}|${posNewerThanPricedAt ? posPricedAt : pricedAt}|${latestPrice}`;
            }
            return '0|0||0';
          },
          cellRenderer: (params: ICellRendererParams) => (
            <RePrice
              posPricedAt={params.data.posPricedAt}
              pricedAt={params.data.pricedAt}
              unitPrice={params.data.unitPrice}
              previousPrice={params.data.previousPrice}
              pricedBy={params.data.pricedBy}
              latestPrice={params.data.latestPrice}
              textOnly
            />
          ),
        },
        {
          colId: 'properties',
          allowSorting: false,
          headerName: 'Properties',
          enableCellChangeFlash: false,
          valueGetter: (params) => {
            const d = params.data;
            const tags = d?.tags;
            const vendorProperties = d?.vendorProperties;
            // Pipe-join the primitives (cheaper than JSON.stringify of a composite object) and only
            // JSON-serialize the array/object fields — that was the real cost, and it's skipped
            // entirely when they're empty (the common non-DTI, untagged case). Full change-detection
            // fidelity is preserved.
            return `${d?.isBroadcasting}|${d?.ruleCount}|${d?.quantityReserved}|${d?.quantityRemaining}|${d?.numBarcodes}|${d?.ticketStockId}|${
              tags ? JSON.stringify(tags) : ''
            }|${vendorProperties ? JSON.stringify(vendorProperties) : ''}`;
          },
          cellRenderer: (params: ICellRendererParams<BarkerEventListing>) => {
            if (params.data && !params.data?.listingId.endsWith('sales') && !params.data?.sale) {
              const { isBroadcasting, tenantId, listingId, ruleCount, tags, quantityReserved, quantityRemaining, numBarcodes, vendorProperties, ticketStockId } = params.data;

              const dtiProperties =
                isDtiHosted && vendorProperties?.pointOfSaleId === BrokerNerdsBarkerCoreEnumsPointOfSale.DtiPortal
                  ? (vendorProperties as BrokerNerdsBarkerCoreModelsInventoryListingVendorPropertiesDtiPortal)
                  : undefined;

              return (
                <Properties
                  tenantId={tenantId}
                  listingId={listingId}
                  isBroadcasting={isBroadcasting}
                  updateBroadcasting={updateBroadcasting}
                  tags={tags}
                  quantityReserved={quantityReserved}
                  quantityRemaining={quantityRemaining}
                  ruleCount={ruleCount}
                  numBarcodes={dtiProperties ? 0 : numBarcodes} // DTI has its own barcodes column inside of attachments
                  vendorProperties={dtiProperties}
                  ticketStockId={hasTicketStockPropertyFlag ? ticketStockId : undefined}
                />
              );
            }
            return null;
          },
          width: 120,
          minWidth: 120,
        },
        {
          colId: 'inHand',
          headerName: 'In Hand',
          width: 110,
          hide: true,
          cellStyle: { textAlign: 'center' },
          valueGetter: (params) => `${params.data?.isInHand}|${params.data?.inHandDate}`,
          valueFormatter: (params) => {
            if (!params.data) return '';

            const { isInHand, inHandDate } = params.data;

            return isInHand ? 'Now' : !inHandDate ? '' : dayjs(inHandDate).format('MM/DD/YYYY');
          },
        },
        {
          field: 'floorPrice',
          headerName: 'Floor Price',
          hide: true,
          minWidth: 110,
          width: 110,
          valueFormatter: (params) => formatCurrency(params.value),
          cellStyle: { textAlign: 'right' },
        },
        {
          field: 'ceilingPrice',
          headerName: 'Ceiling Price',
          hide: true,
          minWidth: 110,
          width: 110,
          valueFormatter: (params) => formatCurrency(params.value),
          cellStyle: { textAlign: 'right' },
        },
        { field: 'internalNotes', headerName: 'Internal Notes', hide: !isDtiHosted, minWidth: 120, tooltipField: 'internalNotes' },
        { field: 'externalNotes', headerName: 'External Notes', hide: !isDtiHosted, minWidth: 120, tooltipField: 'externalNotes' },
        {
          field: 'tags',
          headerName: 'Tags',
          hide: true,
          minWidth: 120,
          valueGetter: (params) => params.data?.tags?.join(',') ?? '',
          cellRenderer: (params: ICellRendererParams) => <BNTags tags={params.data?.tags} />,
        },
        {
          field: 'numBarcodes',
          headerName: 'Barcodes',
          hide: true,
          minWidth: 50,
          width: 50,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center', paddingLeft: 0, paddingRight: 0 },
          valueGetter: (params) => params.data?.numBarcodes ?? 0,
          cellRenderer: (params: ICellRendererParams<BarkerEventListing>) => {
            if ((params.data?.numBarcodes ?? 0) > 0) {
              return (
                <Tooltip label="Barcoded" withinPortal withArrow>
                  <Flex w={24} h={24} align="center" justify="center" style={{ borderRadius: 28 }}>
                    <BarcodeIcon size={16} />
                  </Flex>
                </Tooltip>
              );
            }
            return null;
          },
        },
        {
          field: 'cachedAt',
          headerName: 'First Seen',
          hide: true,
          minWidth: 120,
          valueFormatter: (params) => (params.value ? dayjs(params.value).fromNow(true) : 'Unknown'),
          valueGetter: (params) => params.data?.cachedAt?.toString(), // This is to give a constant value to prevent cell flashing / rerendering
          comparator: (a, b) => dayjs(a).unix() - dayjs(b).unix(),
        },
        {
          field: 'ruleUpdatedAt',
          headerName: 'Rule Updated',
          hide: true,
          minWidth: 120,
          valueFormatter: (params) => (params.value ? dayjs(params.value).fromNow(true) : ''),
          valueGetter: (params) => params.data?.ruleUpdatedAt?.toString(), // This is to give a constant value to prevent cell flashing / rerendering
          comparator: (a, b) => dayjs(a).unix() - dayjs(b).unix(),
        },
        ...(isTradeDeskUser
          ? ([
              {
                colId: 'externalListingId',
                headerName: 'External ID',
                hide: true,
                width: 120,
                valueGetter: (params) => {
                  if (!params.data) return '';

                  if (params.data.vendorProperties?.pointOfSaleId !== BrokerNerdsBarkerCoreEnumsPointOfSale.TradeDeskPos) return '';

                  const vendorProperties = params.data.vendorProperties as BrokerNerdsBarkerCoreModelsSalesListingSoldVendorPropertiesTradeDeskPos;

                  return vendorProperties?.listingExternalId;
                },
              },
            ] as BNInventoryColumn[])
          : []),
        ...(isDtiHosted ? dtiDefaultColumnDefs : []),
        {
          colId: 'action',
          allowSorting: false,
          headerName: 'Action',
          enableCellChangeFlash: false,
          valueGetter: (params) => {
            const d = params.data;
            const internalNotes = d?.internalNotes;
            const externalNotes = d?.externalNotes;
            const splits = d?.splits;
            // Pipe-join primitives; JSON-serialize only the free-text/object fields so the change
            // key stays collision-safe (notes can contain '|') without stringifying a whole object.
            return `${d?.isBroadcasting}|${d?.ruleId}|${d?.ruleCount}|${d?.quantityReserved}|${internalNotes ? JSON.stringify(internalNotes) : ''}|${
              externalNotes ? JSON.stringify(externalNotes) : ''
            }|${splits ? JSON.stringify(splits) : ''}`;
          },
          cellRenderer: (params: ICellRendererParams<BarkerEventListing>) => {
            //if (params.data && !params.data.listingId.endsWith('sales') && !params.data.sale) {
            if (params.data) {
              params.eGridCell.parentElement?.setAttribute('aria-label', `Listing ${params.data.listingId} row`);
              return (
                <InventoryAction
                  listing={params.data}
                  updateBroadcasting={updateBroadcasting}
                  setSelectedHistoryListing={setSelectedHistoryListing}
                  ruleId={params.data.ruleId}
                  isAnchorListing={params.data.ruleCount !== undefined && params.data.ruleCount >= 2 && params.node.level !== 0}
                />
              );
            }
            return null;
          },
          pinned: 'right',
          width: 40,
          minWidth: 40,
          maxWidth: 40,
        },
      ] as BNInventoryColumn[],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const columnDefs: ColDef<BarkerEventListing>[] = useMemo(
    () => {
      // Build a single colId -> state-index lookup up front instead of re-scanning inventoryGridState
      // for every column (savedState) and twice per comparison in the sort (findIndex x2).
      const stateIndexById = new Map<string, number>();
      inventoryGridState?.forEach((state, index) => {
        stateIndexById.set(state.colId, index);
      });

      return defaultColumnDefs
        .map((col) => {
          // Clears properties before giving it to AG-Grid to avoid warnings.
          const { allowSorting: _allowSorting, exclude: _exclude, ..._col } = col;
          // dragCell never adopts persisted state (matches the previous filter that excluded it).
          const stateKey = col.colId ?? col.field;
          const savedStateIndex = stateKey !== undefined && stateKey !== 'dragCell' ? stateIndexById.get(stateKey) : undefined;
          const savedState = { ...(savedStateIndex !== undefined ? inventoryGridState![savedStateIndex] : undefined) };

          if (_col.colId === 'dragCell') {
            _col.cellRendererParams = {
              showBulkOptions,
            };
          }

          return { ..._col, ...savedState } as ColDef<BarkerEventListing>;
        })
        .sort((a, b) => {
          // Preserve prior semantics: undefined when there is no saved state at all (findIndex on
          // undefined), -1 when the column isn't present in an existing saved state.
          const aIndex = inventoryGridState ? (a.colId !== undefined ? (stateIndexById.get(a.colId) ?? -1) : -1) : undefined;
          const bIndex = inventoryGridState ? (b.colId !== undefined ? (stateIndexById.get(b.colId) ?? -1) : -1) : undefined;

          // If a column isn't in gridState, make it appear at the end, unless it's the action column
          if (bIndex === -1 || aIndex === -1) {
            if (a.colId === 'action') {
              return 1;
            }

            return -1;
          }

          return (aIndex ?? 0) - (bIndex ?? 0);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showBulkOptions, showSort, currentUser],
  );

  const onRowGroupToggled = useCallback((event: RowGroupOpenedEvent<BarkerEventListing, any>) => {
    if (!event.expanded) {
      event.api.forEachDetailGridInfo((detailGridInfo) => {
        detailGridInfo?.api?.destroy();
      });
      // @ts-ignore
      gridRef.current?.api.setGridOption('alignedGrids', []);
    }
  }, []);

  /**
   * Handles selecting the initial inventory listing for marketplace view when grid is loaded and ready
   * @param event
   */
  const onGridReady = useCallback(
    (event: GridReadyEvent) => {
      (window as any).gridRef = event;
      // Handles hot reloading
      if (selectedListingId.hasValue()) {
        event.api.getRowNode(selectedListingId.toString())?.setSelected(true);
      }

      if (hideCostFlag) {
        event.api.setGridOption(
          'columnDefs',
          columnDefs.filter((col) => col.colId !== 'unitCost'),
        );
      }

      setGridReady(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedListingId, hideCostFlag],
  );

  const updateEventSortFn = useCallback(
    (params: InitialGroupOrderComparatorParams<BarkerEventListing>, sort?: SortItem[] | null) => compareInventoryEventGroups(params, sort),
    [],
  );

  const updateEventSort = useCallback(
    (sort: SortItem[]) => {
      gridRef.current?.api.setGridOption('initialGroupOrderComparator', (params: InitialGroupOrderComparatorParams<BarkerEventListing>) => updateEventSortFn(params, sort));
      setEventGridState(sort);
      gridRef.current!.api.refreshClientSideRowModel();
    },
    [setEventGridState, updateEventSortFn],
  );

  /**
   * Controls how the grid groups are sorted.
   * @param params
   * @returns
   */
  const groupSortOrderComparator = useCallback(
    (params: InitialGroupOrderComparatorParams<BarkerEventListing>) => updateEventSortFn(params, eventGridState ?? []),
    [updateEventSortFn, eventGridState],
  );

  const updateViewedEventAndListing = useCallback(
    async (listing: BarkerEventListing) => {
      if (currentUser?.roleId !== 'ReadOnlyUser') {
        try {
          if (listing.eventId && listing.eventId !== '-1') {
            updateLastViewedEventId({ eventId: listing.eventId });
          }
          updateLastViewedListingId({ listingId: listing.listingId });
        } catch {
          // eslint-disable-next-line no-console
          console.log('Ignore Error for now.');
        }
      }
    },
    [currentUser?.roleId, updateLastViewedEventId, updateLastViewedListingId],
  );

  const modifierKeysFlag = useFlag('modifier-keys-bulk');
  const disableDoubleClicking = useRef(false);
  const filterMode = useAtomValue(filterModeAtom);
  const { setMarkedListings } = useBulkState('setMarkedListings');
  const onCellClickedWhenDirty = useCallback(
    async (
      gridEvent: CellClickedEvent<BarkerEventListing> & {
        node: IRowNode<BarkerEventListing> & {
          beans?: {
            gridApi: GridApi<BarkerEventListing>;
          };
        };
      },
    ) => {
      try {
        if (!disableDoubleClicking.current) {
          disableDoubleClicking.current = true;
          if (showBulkOptions && gridEvent.column.getColId() === 'dragCell') {
            disableDoubleClicking.current = false;
            return;
          }
          if (gridEvent.node.data?.listingId.includes('sales')) {
            disableDoubleClicking.current = false;
            return;
          }
          if (
            filterMode === FilterMode.Locked &&
            gridEvent.column.getColId() === 'unitPrice' &&
            gridEvent.node.data?.pricerStatusId === BrokerNerdsBarkerCoreEnumsPricerStatus.None
          ) {
            disableDoubleClicking.current = false;
            return;
          }
          if (!selectedListing && gridEvent.column.getColId() === 'unitPrice' && gridEvent.node.data?.pricerStatusId === BrokerNerdsBarkerCoreEnumsPricerStatus.None) {
            disableDoubleClicking.current = false;
            return;
          }

          if (!showBulkOptions && modifierKeysFlag) {
            const event = gridEvent.event as React.MouseEvent<HTMLDivElement> | null;
            if (event?.ctrlKey) {
              disableDoubleClicking.current = false;
              if (gridEvent.data) {
                setMarkedListings([gridEvent.data.tenantIdListingId]);
              }
              toggleBulkOptions();
              return;
            }
            if (event?.shiftKey) {
              disableDoubleClicking.current = false;

              const lastIndex = gridEvent.api.getRowNode(selectedListingId.toString())?.rowIndex ?? 0;
              const currentIndex = gridEvent.node.rowIndex ?? 0;
              const listingIdsInRange = range(lastIndex, currentIndex)
                .map((x) => gridEvent.api.getDisplayedRowAtIndex(x)?.data?.tenantIdListingId)
                .filter((x) => x != null && !x.endsWith('sales')) as string[];
              setMarkedListings((prev) => [...prev, ...listingIdsInRange]);
              toggleBulkOptions();
              return;
            }
          }

          if (gridEvent.node.data && gridEvent.node.data.tenantIdListingId !== selectedListingId?.toString()) {
            if ((await validateDirtyRuleState()).resolve) {
              await triggerSaveRule();
              closeExpandedNode(selectedListingId);
              setSelectedListingId(gridEvent.node.data.tenantIdListingId);
              requestAnimationFrame(() => {
                gridRef.current?.api.ensureNodeVisible(gridEvent.node);
              });
            }
          }
          disableDoubleClicking.current = false;
        }
      } finally {
        disableDoubleClicking.current = false;
      }
    },
    [
      closeExpandedNode,
      filterMode,
      modifierKeysFlag,
      selectedListing,
      selectedListingId,
      setMarkedListings,
      setSelectedListingId,
      showBulkOptions,
      toggleBulkOptions,
      triggerSaveRule,
      validateDirtyRuleState,
    ],
  );

  useDidUpdate(() => {
    if (selectedListingId.hasValue()) {
      const node = gridRef.current?.api?.getRowNode(selectedListingId.toString());
      if (node && selectedListing) {
        //Attempt to close any open sales
        const salesNode = gridRef.current?.api?.getRowNode(`${selectedListing.tenantIdEventId}|sales`);
        if (salesNode) {
          salesNode.setExpanded(false);
        }
        updateViewedEventAndListing(selectedListing);
        if (previousSelectedListingId) {
          closeExpandedNode(new TenantIdListingId(previousSelectedListingId));
        }
        node.setSelected(true);
        gridRef.current?.api.ensureNodeVisible(node);
        if (node?.isExpandable()) {
          node.setExpanded(true);
        }
      }
    }
    // @ts-ignore
    gridRef.current?.api.setGridOption('alignedGrids', []);
  }, [selectedListingId]);

  useDidUpdate(() => {
    if (previousSelectedEvent) {
      const node = gridRef.current?.api?.getRowNode(`${previousSelectedEvent.tenantId}|${previousSelectedEvent.eventId}|sales`);
      if (node) {
        node.setExpanded(false);
      }
    }
  }, [previousSelectedEvent]);

  useDidUpdate(() => {
    // Refresh cells due to issue with drag handles not showing up.
    // gridRef.current?.api.refreshCells();
    gridRef.current?.api.redrawRows();
  }, [showBulkOptions]);

  /**
   * Used to know when row has been expanded in order to collapse any other expanded rows.
   * Seems like there should be an event for this but unable to find one.
   * @param expandedListingId - Uses this context because innderGridReady happens in same render cycle as {@link detailCellRendererParams}
   * @param params
   */
  const detailGridReady = useCallback(
    (params: GridReadyEvent<BarkerEventListing>) => {
      const dropZoneParams = gridRef.current!.api.getRowDropZoneParams({ onDragStop: onDetailMasterRowDragEnd })!;
      detailGridRef.current = { api: params.api };
      (window as any).detailsGridApi = params;
      params.api.addRowDropZone(dropZoneParams);
      params.api.applyColumnState({ state: [{ colId: 'ruleTier', sort: 'asc', sortIndex: 0 }] });
      const grids: DetailGridInfo[] = [];
      gridRef.current?.api.forEachDetailGridInfo((grid) => {
        grids.push(grid);
      });
      // @ts-ignore
      gridRef.current?.api.setGridOption('alignedGrids', grids);
      // setDetailGridToAlign(grids);
    },
    [onDetailMasterRowDragEnd],
  );

  const soldInventoryGridReady = useCallback((params: GridReadyEvent) => {
    detailGridRef.current = { api: params.api } satisfies Partial<AgGridReact<BarkerEventListing>>;
    (window as any).detailsGridApi = params;
    const grids: DetailGridInfo[] = [];
    gridRef.current?.api.forEachDetailGridInfo((grid) => {
      grids.push(grid);
    });
    // setDetailGridToAlign(grids);
    // @ts-ignore
    gridRef.current?.api.setGridOption('alignedGrids', grids);
  }, []);

  const mergedEventListingsUnfilteredRules = useAtomValue(mergedEventListingsUnfilteredRulesAtom);

  const getDetailRowDataResults = useCallback(
    (listingId: string, ruleId: string | null | undefined) => {
      const groupedListings = mergedEventListingsUnfilteredRules
        // Combine both predicates into one pass so we don't sort the row we're about to drop, and
        // skip an intermediate array allocation. Same result: the dropped row never affects the
        // relative order of the rest under a ruleTier sort.
        .filter((x) => x.ruleId === ruleId && x.listingId !== listingId)
        .sort((a, b) => (a.ruleTier || 0) - (b.ruleTier || 0))
        .map((listing) => {
          const hasPendingBroadcast = pendingUpdates.find((x) => x.listingId === listing.listingId && x.property === 'isBroadcasting');
          return { ...listing, pendingBroadcast: hasPendingBroadcast ? hasPendingBroadcast.value : undefined };
        });
      return groupedListings;
    },
    [mergedEventListingsUnfilteredRules, pendingUpdates],
  );

  const getDetailRowData = useCallback(
    (params: GetDetailRowDataParams) => {
      if (params.node.data.ruleId) {
        const groupedListings = getDetailRowDataResults(params.node.data.listingId, params.node.data.ruleId);
        params.successCallback(groupedListings);
      } else {
        params.successCallback([]);
      }
    },
    [getDetailRowDataResults],
  );

  useDidUpdate(() => {
    try {
      const sel = gridRef.current?.api?.getSelectedNodes?.()?.[0];
      if (sel) {
        const { listingId, ruleId } = sel.data as BarkerEventListing;
        // @ts-ignore: TS2341
        if (!detailGridRef.current?.api.destroyCalled && !isShowingEventPerformance) {
          detailGridRef.current?.api?.setGridOption('rowData', getDetailRowDataResults(listingId, ruleId));
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      // Ignore this error for now. Should be transient
    }
    // Refresh the open detail's data in place (without rebuilding the detail grid) when pending
    // updates or the underlying listings change. detailCellRendererParams intentionally no longer
    // depends on the data, so this effect is what keeps an already-open detail grid up to date.
  }, [pendingUpdates, mergedEventListingsUnfilteredRules]);

  const gridNodeSelection = useCallback(
    (
      _selectedListingId: string,
      options: {
        node?: RowNode<BarkerEventListing>;
        pendingChange: boolean;
        ensureNodeVisible: boolean;
      } = { pendingChange: false, ensureNodeVisible: true },
    ) => {
      if (gridRef.current) {
        const node = options.node ?? (gridRef.current.api.getRowNode(_selectedListingId) as RowNode<BarkerEventListing>);
        if (node) {
          if (!node.isSelected()) {
            node.setSelected(true);
            setSelectedListingId(node.data?.tenantIdListingId);
          }
          if (!node.isExpandable()) {
            // @ts-ignore
            gridRef.current?.api.setGridOption('alignedGrids', []);
          }
          if (options.ensureNodeVisible) {
            requestAnimationFrame(() => {
              gridRef.current?.api.ensureNodeVisible(node);
            });
          }
        } else if (!options.pendingChange) {
          const firstNode = gridRef.current?.api.getRenderedNodes()[1] as RowNode<BarkerEventListing>;
          if (firstNode) {
            firstNode.setSelected(true);
            setSelectedListingId(firstNode.data?.tenantIdListingId);
          }
        }
      }
    },
    [setSelectedListingId],
  );

  const { data: soldInventory } = useGetApiSales(
    {
      eventId: selectedEvent?.eventId ?? '',
      fromDate: dayjs().subtract(2, 'year').startOf('day').toDate(),
      toDate: dayjs().endOf('day').endOf('day').toDate(),
    },
    {
      query: {
        enabled: !!selectedEvent?.eventId && isShowingEventPerformance,
        select(data) {
          return data.data.sales.flatMap((sale) =>
            sale.listings.map((listing) => ({
              ...listing,
              event: selectedEvent,
              sale: {
                ...listing.sale,
                createdAt: sale.createdAt,
                marketplaceName: sale.marketplaceName ?? listing.sale.marketplaceName,
                marketplaceOrderId: sale.marketplaceOrderId ?? listing.sale.marketplaceOrderId,
                saleId: sale.saleId,
              },
            })),
          );
        },
        queryKey: [
          ...getGetApiSalesQueryKey({
            eventId: selectedEvent?.eventId ?? '',
            fromDate: dayjs().subtract(2, 'year').startOf('day').toDate(),
            toDate: dayjs().endOf('day').endOf('day').toDate(),
          }),
          selectedEvent?.tenantId,
        ],
      },
      axios: {
        headers: {
          'x-tenant-id': selectedEvent?.tenantId,
        },
      },
    },
  );

  useDidUpdate(() => {
    if (isShowingEventPerformance && soldInventory && soldInventory.length > 0) {
      const node = gridRef.current?.api.getRowNode(`${selectedEvent?.tenantId}|${selectedEvent?.eventId}|sales`) as RowNode<BarkerEventListing>;
      if (node) {
        node.setExpanded(true);
      }
    } else if (!isShowingEventPerformance && soldInventory && soldInventory.length > 0) {
      const node = gridRef.current?.api.getRowNode(`${selectedEvent?.tenantId}|${selectedEvent?.eventId}|sales`) as RowNode<BarkerEventListing>;
      if (node) {
        node.setExpanded(false);
      }
    }
  }, [soldInventory, selectedEvent, isShowingEventPerformance]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (transientGlobalState.expandGroupByListingId) {
        const node = gridRef.current?.api.getRowNode(transientGlobalState.expandGroupByListingId) as RowNode<BarkerEventListing>;
        if (node) {
          if (!node.id?.includes('sales')) {
            node.setSelected(true);
            node.setExpanded(true);
          }
          setTransientGlobalState((s) => ({ ...s, expandGroupByListingId: null }));
          clearTimeout(timer);
        }
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [setTransientGlobalState, transientGlobalState.expandGroupByListingId]);

  const onRowDataUpdated = useCallback(
    (event: RowDataUpdatedEvent<BarkerEventListing>) => {
      if (!selectedListingId.hasValue() && !selectedEvent) {
        const firstNode = event.api.getRenderedNodes()[1];
        if (firstNode && firstNode.data && !isShowingEventPerformance) {
          setSelectedListingId(firstNode.data.tenantIdListingId);
        }
      } else if (selectedListingId.hasValue() && event.api.getRowNode(selectedListingId.toString())) {
        const node = event.api.getRowNode(selectedListingId.toString()) as RowNode<BarkerEventListing>;
        if (node) {
          if (!node.isSelected()) {
            node.setSelected(true);
            event.api.ensureNodeVisible(node);
            setSelectedListingId(node.data?.tenantIdListingId);
          }
          if (node.isExpandable()) {
            node.setExpanded(true);
          }
        }
      } else if (!selectedEvent) {
        const firstNode = event.api.getRenderedNodes()[1];
        if (firstNode && firstNode.data && !isShowingEventPerformance) {
          setSelectedListingId(firstNode.data.tenantIdListingId);
        }
      } else if (selectedListingId.hasValue()) {
        const node = event.api.getRowNode(selectedListingId.toString()) as RowNode<BarkerEventListing>;
        if (!node) {
          const firstNode = event.api.getRenderedNodes()[1];
          if (firstNode && firstNode.data && !isShowingEventPerformance) {
            setSelectedListingId(firstNode.data.tenantIdListingId);
          }
        }
      }
    },
    [isShowingEventPerformance, selectedEvent, selectedListingId, setSelectedListingId],
  );

  const jotaiStore = useStore();

  useDidUpdate(() => {
    // Read the drag flag from the store WITHOUT subscribing. Subscribing here (useAtomValue)
    // re-renders this container the moment a drag starts, which hands <AgGridReact> a brand-new
    // detailCellRendererParams reference. In AG-Grid v35 a detailCellRendererParams change
    // redraws the rows, destroying the open detail grid (and the row element being dragged)
    // mid-drag — so dragging inside a detail group was impossible. See Inventory.dragdrop.ts.
    if (gridRef.current && gridRef.current.api && !jotaiStore.get(rowDraggingAtom)) {
      if (selectedListingId.hasValue() && !transientGlobalState.ignoreGridScrolling) {
        gridNodeSelection(selectedListingId.toString(), { pendingChange: true, ensureNodeVisible: true });
      }
    }
    // ignoreGridScrolling is a one-shot: inline price edits / comment updates set it so this single
    // data-driven re-scroll is skipped (preserving the user's scroll position). Clear it afterward so
    // subsequent data updates resume re-centering the selected row as normal.
    if (transientGlobalState.ignoreGridScrolling) {
      setTransientGlobalState((s) => ({ ...s, ignoreGridScrolling: false }));
    }
  }, [mergedEventListings]);

  const enableCellTextSelection = useFlag('grid-text-selection');

  // Details grid drag hovers

  // Keep these handlers/data-fetchers in refs so detailCellRendererParams (below) doesn't depend on
  // them. They change identity on selection/data updates (e.g. selecting a listing fetches its rule,
  // which updates the merged listings -> getDetailRowData), so if they were memo deps, simply
  // selecting a row (e.g. clicking a group to expand it) would rebuild detailCellRendererParams and
  // make AG-Grid v35 redraw/recreate the just-opened detail grid repeatedly. The open detail's data
  // is kept fresh by the effect below that calls setGridOption('rowData', ...).
  const detailGridReadyRef = useRef(detailGridReady);
  detailGridReadyRef.current = detailGridReady;
  const onDetailRowDragEndRef = useRef(onDetailRowDragEnd);
  onDetailRowDragEndRef.current = onDetailRowDragEnd;
  const onMasterRowDragMoveRef = useRef(onMasterRowDragMove);
  onMasterRowDragMoveRef.current = onMasterRowDragMove;
  const getDetailRowDataRef = useRef(getDetailRowData);
  getDetailRowDataRef.current = getDetailRowData;

  const detailCellRendererParams = useMemo(
    () => () =>
      ({
        refreshStrategy: 'everything',
        detailGridOptions: {
          getRowId: (x) => x.data.tenantIdListingId,
          alignedGrids: gridRef.current ? [gridRef.current] : undefined,
          rowSelection: { mode: 'singleRow', enableClickSelection: false, checkboxes: false },
          cellSelection: false,
          columnDefs: columnDefs
            .filter((x) => x.field !== 'tenantIdEventId')
            .map((x) => ({
              ...x,
              sort: null,
              sortIndex: null,
            })),
          headerHeight: -1,
          rowBuffer: 0,
          animateRows: false,
          suppressContextMenu: true,
          suppressColumnMoveAnimation: true,
          suppressHorizontalScroll: true,
          enableCellTextSelection,
          alwaysShowVerticalScroll: false,
          rowHeight: 36,
          rowStyle: { lineHeight: '28px', fontSize: 12 },
          onGridReady: (p) => detailGridReadyRef.current(p),
          onRowDragEnd: (e) => onDetailRowDragEndRef.current(e),
          onRowDragMove: (e) => onMasterRowDragMoveRef.current(e),
          onGridPreDestroyed: () => {
            const grids: DetailGridInfo[] = [];
            gridRef.current?.api.forEachDetailGridInfo((grid) => {
              grids.push(grid);
            });
            // @ts-ignore
            gridRef.current?.api.setGridOption('alignedGrids', grids);
          },
          rowClassRules: {
            'active-row': (cssParams) => {
              if (cssParams.data) {
                if (cssParams.data.pendingBroadcast !== undefined) {
                  if (cssParams.data.pendingBroadcast) {
                    return true;
                  }
                } else if (cssParams.data.isBroadcasting) {
                  return true;
                }
              }
              return false;
            },
            'ag-row-listing-at-floor': (cssParams) => {
              if (appearanceSettings && appearanceSettings.highlightListingsAtFloor && cssParams.data && cssParams.data.pricerStatusId === 'AtFloor') {
                return true;
              }
              return false;
            },
            'ag-row-listing-on-hold': (cssParams) => {
              if (appearanceSettings && appearanceSettings.highlightListingsOnHold && cssParams.data && cssParams.data.quantityReserved > 0) {
                return true;
              }
              return false;
            },
            'ag-row-listing-not-broadcasting': (cssParams) => {
              if (appearanceSettings && appearanceSettings.highlightListingsNotBroadcasting && cssParams.data && !cssParams.data.isBroadcasting) {
                return true;
              }
              return false;
            },
          },
          defaultColDef: {
            enableCellChangeFlash: true,
            sortable: false,
            suppressHeaderMenuButton: true,
            resizable: false,
            flex: 1,
          },
          suppressCellFocus: true,
          rowDragText: (dragParams) => {
            const sectionName = dragParams.rowNode?.data.section;
            const rowName = dragParams.rowNode?.data.row;
            const seatsFrom = dragParams.rowNode?.data.seatFrom;
            const seatsTo = dragParams.rowNode?.data.seatThru;
            return `Section ${sectionName} · Row ${rowName} · Seats ${seatsFrom}-${seatsTo}`;
          },
        },
        getDetailRowData: (params: GetDetailRowDataParams) => getDetailRowDataRef.current(params),
      }) satisfies Partial<IDetailCellRendererParams<BarkerEventListingWithPending, BarkerEventListingWithPending>>,
    [columnDefs, enableCellTextSelection, appearanceSettings],
  );

  const getSoldRowData = useCallback(
    (params: GetDetailRowDataParams) => {
      if (soldInventory && soldInventory.length > 0 && selectedEvent) {
        const remappedSoldInventory = soldInventory
          .filter((x) => x.sale)
          .map((x) => ({
            ...x,
            tenantIdListingId: `${x.tenantId}|${x.listingId}|${x.sale.saleId}`,
            quantityRemaining: x.quantity, // Remapped to make columnDefs consistent
          }))
          .sort((a, b) => (a.sale.createdAt < b.sale.createdAt ? 1 : -1));
        params.successCallback(remappedSoldInventory);
      }
    },
    [soldInventory, selectedEvent],
  );

  const soldInventoryDetailCellRendererParams = useMemo(
    () => () =>
      ({
        refreshStrategy: 'everything',
        detailGridOptions: {
          getRowId: (x) => x.data.tenantIdListingId,
          alignedGrids: gridRef.current ? [gridRef.current] : undefined,
          rowSelection: { mode: 'singleRow', enableClickSelection: false, checkboxes: false },
          cellSelection: false,
          columnDefs: columnDefs
            .filter((x) => x.field !== 'tenantIdEventId')
            .map((x) => ({
              ...x,
              sort: null,
              sortIndex: null,
            })),
          headerHeight: -1,
          rowBuffer: 0,
          animateRows: false,
          suppressColumnMoveAnimation: true,
          onGridReady: soldInventoryGridReady,
          onGridPreDestroyed: () => {
            const grids: DetailGridInfo[] = [];
            gridRef.current?.api.forEachDetailGridInfo((grid) => {
              grids.push(grid);
            });
            // @ts-ignore
            gridRef.current?.api.setGridOption('alignedGrids', grids);
          },
          suppressHorizontalScroll: true,
          suppressContextMenu: true,
          noRowsOverlayComponent: () => <BNEmptyState title="No Sold Inventory" titleFz={12} border={false} />,
          rowHeight: 36,
          rowStyle: { lineHeight: '28px', fontSize: 12 },
          rowClass: 'sales-row',
          defaultColDef: {
            enableCellChangeFlash: true,
            sortable: false,
            suppressHeaderMenuButton: true,
            resizable: false,
            flex: 1,
          },
          suppressCellFocus: true,
          masterDetail: true,
          detailCellRenderer: SaleDetails,
          detailRowHeight: 30,
          groupDefaultExpanded: 1,
          embedFullWidthRows: true,
          // This keeps the rows in order to enable proper hover states,
          // but may need future attention because it keeps remnants of the rows
          // and forces extra space even after something else is selected
          // keepDetailRows: true,
          isRowMaster: () => true,
          onCellClicked: (e) => {
            if (e.column.getColId() === 'dragCell') {
              // Toggle ONE open or closed
              // return e.node.setExpanded(!e.node.expanded);

              // Toggle ALL open or closed
              return e.api.forEachNode((node) => {
                node.setExpanded(!node.expanded);
              });
            }
            return false;
          },
        },
        getDetailRowData: getSoldRowData,
      }) satisfies Partial<IDetailCellRendererParams<BarkerEventListingWithPending, BarkerEventListingWithPending>>,
    [columnDefs, soldInventoryGridReady, getSoldRowData],
  );

  const trySaveGridState = useCallback(
    (event: ColumnEvent | SortChangedEvent) => {
      if (gridReady) {
        const state = event.api?.getColumnState()!;
        setInventoryGridState(state);
        if (event.type === 'columnMoved') {
          document.dispatchEvent(onColumnMoved);
        }
        if (event.type === 'sortChanged') {
          document.dispatchEvent(onColumnSorted);
        }
      }
    },
    [gridReady, setInventoryGridState],
  );

  const moveNextSelectedRow = useThrottledCallback(async () => {
    const index = gridRef.current?.api.getSelectedNodes()[0]?.rowIndex ?? 0;
    const topLevelRows = gridRef.current?.api.getRenderedNodes().filter((x) => x.uiLevel === 1 && !x.detail && !x.data?.listingId.includes('sales') && x.rowIndex! > index);
    const node = topLevelRows?.[0] as RowNode<BarkerEventListing>;
    if (node && node.data && node.data.tenantIdListingId !== selectedListingId.toString()) {
      const { tenantIdListingId } = node.data;
      if ((await validateDirtyRuleState()).resolve) {
        await triggerSaveRule();
        setSelectedListingId(tenantIdListingId);
        gridNodeSelection(tenantIdListingId, { node, ensureNodeVisible: false, pendingChange: false });
        requestAnimationFrame(() => {
          gridRef.current?.api.ensureNodeVisible(node);
        });
      }
    }
  }, 50);

  const movePreviousSelectedRow = useThrottledCallback(async () => {
    const index = gridRef.current?.api.getSelectedNodes()[0]?.rowIndex ?? 0;
    const topLevelRows = gridRef.current?.api
      .getRenderedNodes()
      .filter((x) => x.uiLevel === 1 && !x.detail && !x.data?.listingId.includes('sales') && x.rowIndex! < index)
      .sort((a, b) => b.rowIndex! - a.rowIndex!);
    const node = topLevelRows?.[0] as RowNode<BarkerEventListing>;
    if (!node?.data || node.data.tenantIdListingId === selectedListingId.toString()) {
      return;
    }
    if ((await validateDirtyRuleState()).resolve) {
      const { tenantIdListingId } = node.data;
      await triggerSaveRule();
      setSelectedListingId(tenantIdListingId);
      gridNodeSelection(tenantIdListingId, { node, ensureNodeVisible: false, pendingChange: false });
      requestAnimationFrame(() => {
        gridRef.current?.api.ensureNodeVisible(node);
      });
    }
  }, 50);

  useEffect(() => {
    document.addEventListener(onNextInventoryItem.type, moveNextSelectedRow);
    document.addEventListener(onPrevInventoryItem.type, movePreviousSelectedRow);

    return () => {
      document.removeEventListener(onNextInventoryItem.type, moveNextSelectedRow);
      document.removeEventListener(onPrevInventoryItem.type, movePreviousSelectedRow);
    };
  }, [moveNextSelectedRow, movePreviousSelectedRow]);

  const queryClient = useQueryClient();

  const { data: commentReceipts } = useGetApiInventoryEventsCommentsReceipts({
    query: {
      enabled: selectedTenantIds.length > 0,
      select(data) {
        return data.data;
      },
    },
  });

  const updateEventCommentReceipt = (tenantId: string, eventId: string) => {
    queryClient.setQueryData<AxiosResponse<BrokerNerdsBarkerCoreModelsInventoryEventCommentReceipt[]>>(getGetApiInventoryEventsCommentsReceiptsQueryKey(), (old) => {
      if (!old || !old.data) {
        return old;
      }

      return {
        ...old,
        data: [...old?.data.filter((x) => !(x.tenantId === tenantId && x.eventId === eventId)), { tenantId, eventId, readAt: new Date() }],
      };
    });
  };

  return {
    groupSortOrderComparator,
    gridRef,
    mergedEventListings: filteredListings,
    columnDefs,
    onGridReady,
    onMasterRowDragEnd,
    onMasterRowDragMove,
    detailCellRendererParams,
    defaultColDef,
    selectedHistoryListing,
    setSelectedHistoryListing,
    toggleShowSort,
    onCellClickedWhenDirty,
    setInventoryQuickFilter,
    setFilter,
    filter,
    inventoryQuickFilter,
    gridReady,
    clearInventoryGridState,
    trySaveGridState,
    updateEventSort,
    eventGridState,
    inventoryGridState,
    clearEventGridState,
    eventHeaderSettings,
    setEventHeaderSettings,
    resetEventHeaderSettings,
    defaultColumnDefs,
    showBulkOptions,
    toggleBulkOptions,
    soldInventoryDetailCellRendererParams,
    isShowingEventPerformance,
    getDetailRowDataResults,
    onRowGroupToggled,
    onRowDataUpdated,
    showPurchaseModal,
    openPurchaseModal,
    closePurchaseModal,
    setInitialPurchaseEvent,
    initialPurchaseEvent,
    updateBroadcasting,
    commentReceipts,
    updateEventCommentReceipt,
    vendors,
    isVendorsLoading,
  };
};

export const { Provider: InventoryStateProvider, useSelector: useInventory } = yasml(InventoryState);
